import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import { STRATEGY_OPTIONS, strategyLabel } from "../../constants/strategies";

/* ── helpers ── */
const fmt2   = (n) => n == null ? "—" : Number(n).toFixed(2);
const fmt0   = (n) => n == null ? "—" : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.abs(n));
const fmtPnl = (n) => n == null ? "—" : `${n >= 0 ? "+" : "-"}₹${fmt0(n)}`;
const fmtRs  = (n) => n == null ? "—" : `₹${fmt0(n)}`;
const pnlClr = (n) => n == null ? "#333" : n >= 0 ? "#28a745" : "#dc3545";

/* No stock/futures position and no active call sold — nothing on the books yet.
   call_strike is a preview candidate the rule engine recomputes every cycle even
   while flat, so it's non-null well before any call is actually sold — don't use
   it here. active_call_lots (from runtime) is the authoritative live-call signal. */
const isFlatRow = (row, strategyType) => {
  if (strategyType === "DELTA_HEDGED_SHORT_STRANGLE") {
    return (row.total_call_lots || 0) === 0 && (row.total_put_lots || 0) === 0;
  }
  return (row.netqty || 0) === 0 && (row.active_call_lots || 0) === 0;
};

/* ── DHSS column definitions ── */
const DHSS_COLS = [
  { key: "symbol",                 label: "Stock",                   fmt: (v) => <strong>{v}</strong>,          align: "left"  },
  { key: "lot_size",               label: "Lot Size",                fmt: (v) => v ?? "—",                      align: "right" },
  { key: "lots_per_side",          label: "Lots Per Side",           fmt: (v) => v ?? "—",                      align: "right" },
  { key: "days_to_expiry",         label: "Days to Expiry",          fmt: (v) => v ?? "—",                      align: "right" },
  { key: "underlying_ltp",         label: "Underlying LTP",          fmt: (v) => fmtRs(v),                      align: "right" },
  { key: "max_profit",             label: "Max Profit",              fmt: (v) => <span style={{ color: "#28a745" }}>{fmtRs(v)}</span>, align: "right" },
  { key: "upper_breakeven",        label: "Upper Breakeven",         fmt: (v) => fmtRs(v),                      align: "right" },
  { key: "lower_breakeven",        label: "Lower Breakeven",         fmt: (v) => fmtRs(v),                      align: "right" },
  { key: "delta_imbalance",        label: "Delta Imbalance",         fmt: (v) => v == null ? "—" : v.toFixed(4), align: "right" },
  { key: "delta_neutral",          label: "Delta Neutral",           fmt: (v, row) => {
      const imb = row.delta_imbalance;
      const thr = row.delta_neutral_threshold;
      if (imb == null || thr == null) return "—";
      const ok = imb <= thr;
      return <span style={{ fontWeight: 700, color: ok ? "#28a745" : "#dc3545" }}>{ok ? "Yes" : "No"}</span>;
    }, align: "center" },
  { key: "delta_neutral_threshold", label: "Threshold",             fmt: (v) => v == null ? "—" : v.toFixed(4), align: "right" },
  { key: "total_call_lots",        label: "Total Calls",             fmt: (v) => v ?? "—",                      align: "right" },
  { key: "total_put_lots",         label: "Total Puts",              fmt: (v) => v ?? "—",                      align: "right" },
  { key: "call_delta",             label: "Call Delta",              fmt: (v) => v == null ? "—" : Number(v).toFixed(4), align: "right" },
  { key: "put_delta",              label: "Put Delta",               fmt: (v) => v == null ? "—" : Number(v).toFixed(4), align: "right" },
  { key: "unrealised_profit",      label: "Unrealised Profit",       fmt: (v) => <span style={{ color: pnlClr(v) }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "premium_collected",      label: "Premium Collected (Open)", fmt: (v) => <span style={{ color: "#6f42c1" }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "booked_profit",          label: "Booked Profit (Closed)",  fmt: (v) => <span style={{ color: pnlClr(v) }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "net_potential_profit",   label: "Net Potential Profit",    fmt: (v) => <span style={{ color: "#17a2b8", fontWeight: 700 }}>{fmtPnl(v)}</span>, align: "right" },
];

/* ── Pyramid Hedging column definitions ── */
const PH_COLS = [
  { key: "symbol",                  label: "Stock",                       fmt: (v) => <strong>{v}</strong>,             align: "left"  },
  { key: "stock_ltp",               label: "Stock LTP",                   fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "qty_lots",                sortBy: "netqty",                     label: "Qty (Lots)",                  fmt: (_, row) => row.netqty == null ? "—" : `${row.netqty}${row.lots != null ? ` (${Number(row.lots).toFixed(2)}L)` : ""}`, align: "right" },
  { key: "avg_price",               label: "Avg. Price",                  fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "effective_avg_price",     label: "Effective Avg. Price",        fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "invested",                label: "Invested",                    fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "lot_size",                label: "Lot Size",                    fmt: (v) => v ?? "—",                         align: "right" },
  { key: "call_strike",             label: "Call Strike",                 fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "call_delta",              label: "Call Delta",                  fmt: (v) => v == null ? "—" : Number(v).toFixed(3), align: "right" },
  { key: "put_delta",               label: "Put Delta (CSP)",             fmt: (v) => v == null ? "—" : Number(v).toFixed(3), align: "right" },
  { key: "target_lots",             label: "Target Lots",                 fmt: (v) => v ?? "—",                         align: "right" },
  { key: "hedge_pct",               label: "Hedge %",                     fmt: (v) => v == null ? "—" : `${v.toFixed(1)}%`, align: "right" },
  { key: "call_decay_pct",          label: "Call Decay %",                fmt: (v) => v == null ? "—" : <span style={{ color: pnlClr(v) }}>{v.toFixed(1)}%</span>, align: "right" },
  { key: "reference_avg",           label: "Reference Avg",               fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "gap_pct",                 label: "Gap %",                       fmt: (v) => v == null ? "—" : `${v.toFixed(2)}%`, align: "right" },
  { key: "next_buy_price",          label: "Next Buy Price",              fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "current_expiry_potential_profit", label: "Expiry Potential Profit",   fmt: (v) => <span style={{ color: pnlClr(v), fontWeight: 700 }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "expiry_open_price",       label: "Expiry Open Price",           fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "expiry_effective_price",  label: "Expiry Effective Price",      fmt: (v) => v == null ? "—" : <span style={{ color: "#0055aa", fontWeight: 600 }}>₹{v}</span>, align: "right" },
  { key: "current_cycle_premium",   label: "Current Cycle Premium",       fmt: (v) => <span style={{ color: "#6f42c1" }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "stock_realized_profit",   label: "Stock Realized Profit",       fmt: (v) => <span style={{ color: pnlClr(v) }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "net_realized_profit",     label: "Net Realized Profit",         fmt: (v) => <span style={{ color: pnlClr(v), fontWeight: 700 }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "unrealised_profit",       label: "Unrealised Profit",           fmt: (v) => <span style={{ color: pnlClr(v) }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "next_buy_quantity",       label: "Next Buy Qty",                fmt: (v) => v ?? "—",                         align: "right" },
  { key: "days_to_expiry",          label: "Days to Expiry",              fmt: (v) => v ?? "—",                         align: "right" },
  { key: "tail_risk_pnl",           label: "Tail Risk P&L",               fmt: (v) => <span style={{ color: pnlClr(v), fontWeight: 700 }}>{fmtPnl(v)}</span>, align: "right" },
];

/* ── Futures Pyramid Hedging column definitions ── */
const FPH_COLS = [
  { key: "symbol",                  label: "Stock",                       fmt: (v) => <strong>{v}</strong>,             align: "left"  },
  { key: "underlying_ltp",          label: "Underlying LTP",              fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "qty_lots",                sortBy: "combined_qty",               label: "Futures Qty (Lots)",          fmt: (_, row) => row.combined_qty == null ? "—" : `${row.combined_qty}${row.combined_lots != null ? ` (${Number(row.combined_lots).toFixed(2)}L)` : ""}`, align: "right" },
  { key: "avg_price",               label: "Avg. Price",                  fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "effective_avg_price",     label: "Effective Avg. Price",        fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "invested",                label: "Invested",                    fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "lot_size",                label: "Lot Size",                    fmt: (v) => v ?? "—",                         align: "right" },
  { key: "call_strike",             label: "Call Strike",                 fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "call_delta",              label: "Call Delta",                  fmt: (v) => v == null ? "—" : Number(v).toFixed(3), align: "right" },
  { key: "target_lots",             label: "Target Lots",                 fmt: (v) => v ?? "—",                         align: "right" },
  { key: "hedge_pct",               label: "Hedge %",                     fmt: (v) => v == null ? "—" : `${v.toFixed(1)}%`, align: "right" },
  { key: "call_decay_pct",          label: "Call Decay %",                fmt: (v) => v == null ? "—" : <span style={{ color: pnlClr(v) }}>{v.toFixed(1)}%</span>, align: "right" },
  { key: "reference_avg",           label: "Reference Avg",               fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "gap_pct",                 label: "Gap %",                       fmt: (v) => v == null ? "—" : `${v.toFixed(2)}%`, align: "right" },
  { key: "next_buy_price",          label: "Next Buy Price",              fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "next_buy_distance_pct",   label: "Next Buy Distance %",         fmt: (v, row) => {
    if (v == null) return "—";
    const danger = row.underlying_ltp != null && row.next_buy_price != null && row.underlying_ltp > row.next_buy_price;
    return <span style={{ color: danger ? "#dc3545" : "inherit", fontWeight: danger ? 700 : "normal" }}>{v.toFixed(2)}%</span>;
  }, align: "right" },
  { key: "next_buy_quantity",       label: "Next Buy Qty",                fmt: (v) => v ?? "—",                         align: "right" },
  { key: "stock_offset_qty",        label: "Stock Offset Qty",            fmt: (v) => v ?? "—",                         align: "right" },
  { key: "stock_offset_avg_price",  label: "Stock Offset Avg",            fmt: (v) => fmtRs(v),                         align: "right" },
  { key: "current_expiry_potential_profit_with_realized", label: "Expiry Potential Profit",   fmt: (v) => <span style={{ color: pnlClr(v), fontWeight: 700 }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "expiry_effective_price",  label: "Expiry Effective Price",      fmt: (v) => v == null ? "—" : <span style={{ color: "#0055aa", fontWeight: 600 }}>₹{v}</span>, align: "right" },
  { key: "current_cycle_premium",   label: "Current Cycle Premium",       fmt: (v) => <span style={{ color: "#6f42c1" }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "downside_min_csm_price",  label: "Downside Min Profit Price",   fmt: (v, row) => {
    if (v == null) return "—";
    const danger = row.underlying_ltp != null && row.underlying_ltp < v;
    return <span style={{ color: danger ? "#dc3545" : "inherit", fontWeight: danger ? 700 : "normal" }}>₹{fmt0(v)}</span>;
  }, align: "right" },
  { key: "downside_min_csm_distance_pct", label: "Downside Min Distance %", fmt: (v) => v == null ? "—" : `${v.toFixed(2)}%`, align: "right" },
  { key: "futures_realized_profit", label: "Futures Realized Profit",     fmt: (v) => <span style={{ color: pnlClr(v) }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "net_realized_profit",     label: "Net Realized Profit",         fmt: (v) => <span style={{ color: pnlClr(v), fontWeight: 700 }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "realized_pnl_by_expiry",  label: "Realized P&L by Expiry",     fmt: (v) => {
    if (!v || typeof v !== "object") return "—";
    const entries = Object.entries(v).filter(([k]) => !k.startsWith("_"));
    if (!entries.length) return "—";
    return <span style={{ fontSize: "0.8em", whiteSpace: "nowrap" }}>
      {entries.map(([k, pnl]) => {
        const label = k.length >= 10 ? k.slice(5, 7) === "01" ? `Jan${k.slice(0,4)!=="2025"?k.slice(2,4):""}` : new Date(k + "T00:00:00").toLocaleString("en-IN",{month:"short"}) : k;
        return <span key={k} style={{ marginRight: 6, color: pnlClr(pnl) }}>{label}: {fmtPnl(pnl)}</span>;
      })}
    </span>;
  }, align: "left" },
  { key: "unrealised_profit",       label: "Unrealised Profit",           fmt: (v) => <span style={{ color: pnlClr(v) }}>{fmtPnl(v)}</span>, align: "right" },
  { key: "days_to_expiry",          label: "Days to Expiry",              fmt: (v) => v ?? "—",                         align: "right" },
  { key: "tail_risk_pnl",           label: "Tail Risk P&L",               fmt: (v) => <span style={{ color: pnlClr(v), fontWeight: 700 }}>{fmtPnl(v)}</span>, align: "right" },
];

const COLS_BY_TYPE = {
  DELTA_HEDGED_SHORT_STRANGLE: DHSS_COLS,
  PYRAMID_HEDGING:             PH_COLS,
  FUTURES_PYRAMID_HEDGING:     FPH_COLS,
};

const LS_ORDER  = (t) => `strategy_summary_col_order_${t}`;
const LS_HIDDEN = (t) => `strategy_summary_hidden_cols_${t}`;

function loadOrder(type, cols) {
  try {
    const saved = localStorage.getItem(LS_ORDER(type));
    if (saved) {
      const order = JSON.parse(saved);
      const keys  = cols.map(c => c.key);
      // merge: start with saved order, append any new columns at end
      const valid = order.filter(k => keys.includes(k));
      const added = keys.filter(k => !valid.includes(k));
      return [...valid, ...added];
    }
  } catch (_) {}
  return cols.map(c => c.key);
}

function loadHidden(type) {
  try {
    const saved = localStorage.getItem(LS_HIDDEN(type));
    if (saved) return new Set(JSON.parse(saved));
  } catch (_) {}
  return new Set();
}

export default function StrategySummary() {
  const { selectedAccount } = useAccount();
  const isAllAccounts = selectedAccount === "All Accounts";

  const [strategyType, setStrategyType] = useState("FUTURES_PYRAMID_HEDGING");
  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  const allCols = COLS_BY_TYPE[strategyType] || [];

  const [colOrder, setColOrder]   = useState(() => loadOrder(strategyType, allCols));
  const [hiddenCols, setHiddenCols] = useState(() => loadHidden(strategyType));
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [sortKey, setSortKey]   = useState(null);
  const [sortDir, setSortDir]   = useState("asc");

  const dragSrcIdx = useRef(null);
  const pickerRef  = useRef(null);
  const topScrollRef = useRef(null);
  const tableWrapRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const syncingScroll = useRef(null);

  // Reset column state when strategy type changes
  useEffect(() => {
    const cols = COLS_BY_TYPE[strategyType] || [];
    setColOrder(loadOrder(strategyType, cols));
    setHiddenCols(loadHidden(strategyType));
    setColPickerOpen(false);
  }, [strategyType]);

  // Close picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setColPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedAccount || isAllAccounts) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get("/strategy/summary", {
        params: { username: selectedAccount, strategy_type: strategyType, status: statusFilter }
      });
      setRows(res.data);
      setLastRefreshed(new Date());
    } catch (e) {
      setError("Failed to load strategy summary.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, strategyType, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const id = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchData]);

  /* ── column visibility ── */
  const toggleCol = (key) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      localStorage.setItem(LS_HIDDEN(strategyType), JSON.stringify([...next]));
      return next;
    });
  };

  /* ── drag-and-drop column reorder ── */
  const onDragStart = (e, idx) => {
    dragSrcIdx.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const onDrop = (e, targetIdx) => {
    e.preventDefault();
    const src = dragSrcIdx.current;
    if (src == null || src === targetIdx) return;
    setColOrder(prev => {
      const next = [...prev];
      const [moved] = next.splice(src, 1);
      next.splice(targetIdx, 0, moved);
      localStorage.setItem(LS_ORDER(strategyType), JSON.stringify(next));
      return next;
    });
    dragSrcIdx.current = null;
  };

  /* ── sort toggle ── */
  const SORTABLE = ["symbol", "invested", "tail_risk_pnl", "unrealised_profit",
                   "current_expiry_potential_profit", "current_expiry_potential_profit_with_realized",
                   "net_realized_profit", "netqty", "combined_qty",
                   "stock_ltp", "next_buy_price", "gap_pct", "hedge_pct", "call_decay_pct", "days_to_expiry",
                   "next_buy_distance_pct", "downside_min_csm_price"];
  const toggleSort = (key) => {
    if (!SORTABLE.includes(key)) return;
    setSortKey(prev => {
      if (prev === key) { setSortDir(d => d === "asc" ? "desc" : "asc"); return key; }
      setSortDir("asc");
      return key;
    });
  };

  /* ── ordered + visible columns ── */
  const colMap    = Object.fromEntries(allCols.map(c => [c.key, c]));
  const ordCols   = colOrder.map(k => colMap[k]).filter(Boolean);
  const visCols   = ordCols.filter(c => !hiddenCols.has(c.key));

  const sortedRows = sortKey ? [...rows].sort((a, b) => {
    const va = a[sortKey] ?? (sortKey === "symbol" ? "" : -Infinity);
    const vb = b[sortKey] ?? (sortKey === "symbol" ? "" : -Infinity);
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === "asc" ? va - vb : vb - va;
  }) : rows;

  // Keep the top scrollbar's inner width matched to the table's real scroll width
  // (column visibility/order/data changes can all change how wide the table is).
  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;
    const update = () => setScrollWidth(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visCols, sortedRows.length, loading]);

  const onTopScroll = () => {
    if (syncingScroll.current === "table") return;
    syncingScroll.current = "top";
    if (tableWrapRef.current && topScrollRef.current) {
      tableWrapRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    syncingScroll.current = null;
  };

  const onTableScroll = () => {
    if (syncingScroll.current === "top") return;
    syncingScroll.current = "table";
    if (tableWrapRef.current && topScrollRef.current) {
      topScrollRef.current.scrollLeft = tableWrapRef.current.scrollLeft;
    }
    syncingScroll.current = null;
  };

  /* ── render ── */
  if (isAllAccounts) {
    return (
      <div style={page}>
        <h2 style={titleStyle}>Strategy Summary</h2>
        <div style={emptyBox}>Please select a specific account to view strategy summaries.</div>
      </div>
    );
  }

  return (
    <div style={page}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={titleStyle}>Strategy Summary</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          Per-stock summary for all {strategyLabel(strategyType)} strategies
          {selectedAccount ? ` · ${selectedAccount}` : ""}
        </p>
      </div>

      {/* Controls row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {/* Strategy selector */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 13, color: "#555", fontWeight: 600 }}>Strategy:</label>
          <select value={strategyType} onChange={e => setStrategyType(e.target.value)} style={selectStyle}>
            {STRATEGY_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Status filter */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 13, color: "#555", fontWeight: 600 }}>Status:</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ALL">All</option>
          </select>
        </div>

        <span style={{ fontSize: 13, color: "#aaa" }}>
          {loading ? "Loading…" : `${rows.length} strateg${rows.length !== 1 ? "ies" : "y"}`}
          {lastRefreshed && !loading && (
            <span style={{ marginLeft: 8, fontSize: 11 }}>
              · updated {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
        </span>

        {/* Refresh */}
        <button onClick={fetchData} style={refreshBtn} disabled={loading}>
          {loading ? "…" : "Refresh"}
        </button>

        {/* Columns picker */}
        <div style={{ position: "relative", marginLeft: "auto" }} ref={pickerRef}>
          <button
            onClick={() => setColPickerOpen(o => !o)}
            style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #ccc", fontSize: 12, cursor: "pointer", background: "#fff" }}
          >
            Columns ▾
          </button>
          {colPickerOpen && (
            <div style={pickerDropdown}>
              <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6, textTransform: "uppercase" }}>
                Toggle visibility
              </div>
              {ordCols.map(c => (
                <label key={c.key} style={pickerRow}>
                  <input type="checkbox" checked={!hiddenCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && <div style={errorBox}>{error}</div>}

      {/* Top scrollbar (mirrors the table's horizontal scroll) */}
      {rows.length > 0 && scrollWidth > 0 && (
        <div ref={topScrollRef} onScroll={onTopScroll} style={topScrollBar}>
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      )}

      {/* Table */}
      <div ref={tableWrapRef} onScroll={onTableScroll} style={tableWrap}>
        {loading ? (
          <div style={centered}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={centered}>No {strategyLabel(strategyType)} strategies found for {selectedAccount}.</div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                {visCols.map((col, idx) => {
                  const sortField  = col.sortBy || col.key;
                  const isSortable = SORTABLE.includes(sortField);
                  const isActive   = sortKey === sortField;
                  return (
                    <th
                      key={col.key}
                      draggable
                      onDragStart={e => onDragStart(e, ordCols.indexOf(col))}
                      onDragOver={onDragOver}
                      onDrop={e => onDrop(e, ordCols.indexOf(col))}
                      onClick={isSortable ? () => toggleSort(sortField) : undefined}
                      style={{
                        ...th,
                        textAlign: "center",
                        cursor: isSortable ? "pointer" : "grab",
                        userSelect: "none",
                        color: isActive ? "#2c3e50" : th.color,
                      }}
                      title={isSortable ? "Click to sort" : "Drag to reorder"}
                    >
                      {col.label}
                      {isSortable && (
                        <span style={{ marginLeft: 4, opacity: isActive ? 1 : 0.35 }}>
                          {isActive ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, ri) => (
                <tr
                  key={row.id ?? ri}
                  style={{ background: row.status === "ACTIVE" ? "#fff" : "#fafafa" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                  onMouseLeave={e => e.currentTarget.style.background = row.status === "ACTIVE" ? "#fff" : "#fafafa"}
                >
                  {visCols.map(col => {
                    const isLtpCol = col.key === "stock_ltp" ||
                      (col.key === "underlying_ltp" && strategyType === "FUTURES_PYRAMID_HEDGING");
                    return (
                      <td key={col.key} style={{ ...td, textAlign: "center" }}>
                        {col.key === "symbol" ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <a
                              href={`/strategy/manage?symbol=${row.symbol}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#2c6fad", textDecoration: "none", fontWeight: 700 }}
                            >
                              {row.symbol}
                            </a>
                            {isFlatRow(row, strategyType) && (
                              <span
                                title="No trades placed for this strategy yet"
                                style={flatBadge}
                              >
                                FLAT
                              </span>
                            )}
                          </span>
                        ) : isLtpCol ? (
                          <a
                            href={`/trading/${row.symbol}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#2c6fad", textDecoration: "none" }}
                          >
                            {col.fmt ? col.fmt(row[col.key], row) : (row[col.key] ?? "—")}
                          </a>
                        ) : col.fmt ? col.fmt(row[col.key], row) : (row[col.key] ?? "—")}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Drag hint */}
      {rows.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#aaa", textAlign: "right" }}>
          Drag column headers to reorder · use Columns ▾ to show/hide
        </div>
      )}
    </div>
  );
}

/* ── styles ── */
const page       = { padding: "20px 25px 30px", background: "#f4f6f9", minHeight: "100vh" };
const titleStyle = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const selectStyle = {
  padding: "7px 10px", borderRadius: 8, border: "1px solid #ccc",
  fontSize: 13, background: "#fff", cursor: "pointer", minWidth: 160,
};
const refreshBtn = {
  padding: "7px 14px", borderRadius: 8, border: "none",
  background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 13,
};
const tableWrap = {
  background: "#fff", borderRadius: 12,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflowX: "auto",
};
const topScrollBar = { overflowX: "auto", overflowY: "hidden", marginBottom: 8 };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = {
  padding: "10px 12px", background: "#f8f9fa",
  borderBottom: "2px solid #dee2e6", fontWeight: 600,
  fontSize: 11, color: "#6c757d", textTransform: "uppercase",
  whiteSpace: "nowrap",
};
const td = { padding: "9px 12px", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" };
const centered   = { padding: 40, textAlign: "center", color: "#888" };
const emptyBox   = { padding: "40px 20px", textAlign: "center", color: "#888", background: "#fff", borderRadius: 12 };
const errorBox   = { padding: "10px 16px", borderRadius: 8, background: "#fde8e8", color: "#dc3545", marginBottom: 12, fontSize: 13 };
const pickerDropdown = {
  position: "absolute", right: 0, top: "110%", zIndex: 300,
  background: "#fff", border: "1px solid #ddd", borderRadius: 8,
  boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: "10px 16px",
  minWidth: 220, maxHeight: 400, overflowY: "auto",
};
const pickerRow = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "4px 0", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
};
const flatBadge = {
  fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
  color: "#856404", background: "#fff3cd", border: "1px solid #ffe08a",
  borderRadius: 5, padding: "2px 6px", cursor: "default",
};
